import React, { ReactElement, useEffect, useState } from "react";

import "../styles.scss";
import "../scripts";

import {
  getDefaultProps,
  KameleonElement,
  primaryCollectionBlock,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";
import { WebflowComponentProps, webflowHOC } from "@kameleon-common/webflow-js";
import { Variants } from "@kameleon-util/global-theme";
import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";

import {
  blockConfig,
  ContentCollectionItem,
  ContentCollectionItemType,
} from "./components/content-collection-item";

import {
  ContentCollectionType as ComponentType,
  layoutOptions,
} from "./types/prop-types";
// eslint-disable-next-line import/no-extraneous-dependencies
import getEvents from "./js/webflow";

type ContentCollectionType = {
  PrimaryBlocks: ContentCollectionItemType[];
} & ComponentType;

type PassThroughProps = {
  tabIds: { tabId: string }[];
  imageIds: { imageId: number }[];
};
type CollectionWebflowType = WebflowComponentProps<
  ContentCollectionType,
  PassThroughProps
>;

const getTabsIds = (primaryBlocks: any[]) => {
  const updatedPassThroughState: PassThroughProps = {
    tabIds: [],
    imageIds: [],
  };
  primaryBlocks.forEach((_, idx) => {
    updatedPassThroughState.tabIds.push({ tabId: `t${idx}` });
    updatedPassThroughState.imageIds.push({ imageId: idx });
  });
  return updatedPassThroughState;
};

const Component = ({
  PrimaryBlocks,
  passThrough,
}: CollectionWebflowType): ReactElement => {
  const [passThroughState, setPassThroughState] = useState<PassThroughProps>(
    getTabsIds(PrimaryBlocks)
  );
  const { tabIds } = passThroughState ?? {};

  useEffect(() => {
    const updatedPassThroughState = getTabsIds(PrimaryBlocks);
    setPassThroughState(updatedPassThroughState);
    passThrough?.(updatedPassThroughState);
  }, [PrimaryBlocks]);

  return (
    <Box className="primary-collection-content-collection-two-col-scroll-tabs-v1 section_layout350">
      <Box className="padding-global container-large padding-section-large">
        <Variants.BoxGreyFilled className="layout350_tabs-menu">
          {PrimaryBlocks.map((pb, index) => (
            <Variants.LinkNav
              id={`tab-${pb.tabTitle}`}
              key={`tab-${index}`}
              href={`#Content-Block-${index}`}
              className="layout350_tab-link"
            >
              <Box>{pb.tabTitle}</Box>
            </Variants.LinkNav>
          ))}
        </Variants.BoxGreyFilled>

        <Box className="w-layout-grid layout350_component">
          <Box className="layout350_content-wrapper">
            {PrimaryBlocks?.map((primaryBlockProps, i) => {
              const itemProps = {
                ...primaryBlockProps,
                _tabId: tabIds?.[i].tabId,
              };

              return (
                <ContentCollectionItem
                  key={`item-${i}`}
                  _index={i}
                  {...itemProps}
                />
              );
            })}
          </Box>

          <Box className="layout350_desktop-image-wrapper">
            {PrimaryBlocks?.map((primaryBlock, index) => (
              <Variants.Image
                key={`img-${index}`}
                id={`media__${primaryBlock.title}`}
                sx={{
                  aspectRatio: primaryBlock.media.url.endsWith(".mp4")
                    ? ""
                    : "1 / 1",
                }}
                src={primaryBlock.media.url}
                loading="lazy"
                width="529"
                alt=""
                className={`layout350_image is-image${index}`}
              /> // TODO: list supported video extensions
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ComponentWithWebflow = webflowHOC<
  CollectionWebflowType,
  PassThroughProps
>(Component, [
  {
    module: "ix2",
    getEvents: (pageId, passThrough): object =>
      getEvents(pageId, passThrough?.tabIds ?? [], passThrough?.imageIds ?? []),
  },
]);

export const ContentCollection = primaryCollectionBlock<ContentCollectionType>(
  blockConfig,
  ({ PrimaryBlocks, ...props }: ContentCollectionType) => (
    <ComponentWithWebflow PrimaryBlocks={PrimaryBlocks} {...props} />
  )
);

ContentCollection.defaultProps =
  getDefaultProps<ContentCollectionType>(defaults);

export const Element: KameleonElement = {
  component: ContentCollection,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
